import deepEqual from "deep-equal";
import {MutableRefObject, useMemo, useRef, useSyncExternalStore} from "react";
import {NodeBushEntry, TreeStore} from "./useVisionBoardData";
import {clamp} from "../../lib/math-utils";
import {NodeSide, VisionBoardNode, VisionBoardNodeId} from "../../cdx-models/utils/extended-fields";
import {VisionBoard, VisionBoardId} from "../../cdx-models/VisionBoard";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {UserId} from "../../cdx-models/User";
import {SelectionStore} from "./vision-board-node-selection-store";
import {Root} from "../../cdx-models/Root";
import {getLocalStorageVal} from "../../lib/storage";

export type BushRect = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type TwoNums = [number, number];

export type PosRef = MutableRefObject<{
  x: number;
  y: number;
  scale: number;
  canvasOffset: TwoNums;
}>;

export const getAnchorPos = ({node, side}: {node: BushRect; side: NodeSide}): TwoNums => {
  switch (side) {
    case "left":
      return [node.minX, node.minY + (node.maxY - node.minY) / 2];
    case "right":
      return [node.maxX, node.minY + (node.maxY - node.minY) / 2];
    case "top":
      return [node.minX + (node.maxX - node.minX) / 2, node.minY];
    case "bottom":
      return [node.minX + (node.maxX - node.minX) / 2, node.maxY];
  }
};

export const getNearestSide = (rect: BushRect, [x, y]: TwoNums): NodeSide => {
  const left = Math.abs(x - rect.minX);
  const right = Math.abs(x - rect.maxX);
  const top = Math.abs(y - rect.minY);
  const bottom = Math.abs(y - rect.maxY);

  const min = Math.min(left, right, top, bottom);
  return left === min ? "left" : right === min ? "right" : top === min ? "top" : "bottom";
};

export const cameraToWorld = ([x, y]: TwoNums, posRef: PosRef): TwoNums => {
  return [
    (x - posRef.current.canvasOffset[0]) / posRef.current.scale - posRef.current.x,
    (y - posRef.current.canvasOffset[1]) / posRef.current.scale - posRef.current.y,
  ];
};

export type CursorEvent = Map<number, {id: UserId; pos: TwoNums}>;
export type SelectionEvent = Map<VisionBoardNodeId, Set<UserId>>;

export type AwarenessStore = {
  setCursor: (id: UserId, pos: TwoNums | null) => void;
  setSelectionIds: (id: UserId, nodeIds: VisionBoardNodeId[]) => void;
  myClientId: number;

  registerCursorListener: (cb: (cursors: CursorEvent) => void) => () => void;
  registerSelectionListener: (cb: (selections: SelectionEvent) => void) => () => void;
};

export type BoundingBoxStore = {
  getSnapshot: () => BushRect;
  subscribe: (cb: (rect: BushRect) => void) => () => void;
};

export type VisionBoardBag = {
  posRef: PosRef;
  store: TreeStore;
  isSelecting: boolean;
  positionCanvas: () => void;
  canvasMeasureRef: MutableRefObject<TwoNums>;
  visionBoard: VisionBoard;
  arenaCtx: ArenaCtx;
  awarenessStore: AwarenessStore;
  selectionStore: SelectionStore;
  root: Root;
  bboxStore: BoundingBoxStore;
  showVisionBoardFilter: (id: VisionBoardNodeId | null) => void;
  shownVisionBoardFilter: VisionBoardNodeId | null;
};

export const clampZoom = (val: number) => clamp(val, 0.1, 2);

export const getDistSquared = (p1: TwoNums, p2: TwoNums) => {
  return (p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2;
};

type CanvasOptions = {};

export const getCanvasOption = <K extends keyof CanvasOptions, TFallback = undefined>(
  store: TreeStore,
  key: K,
  fallback?: TFallback
): NonNullable<CanvasOptions[K]> | TFallback => {
  const opts = store.doc.getMap("options");
  return opts.has(key) ? (opts.get(key) as any) : (fallback as TFallback);
};

export const useCanvasOptions = (store: TreeStore) => {
  const sub = useMemo(() => {
    return (cb: () => void) => {
      const map = store.doc.getMap("options");
      map.observe(cb);
      return () => map.unobserve(cb);
    };
  }, [store]);
  const lastRes = useRef<null | CanvasOptions>(null);
  return useSyncExternalStore(sub, () => {
    const newVal = store.doc.getMap("options").toJSON() as CanvasOptions;
    if (!deepEqual(newVal, lastRes.current)) {
      lastRes.current = newVal;
    }
    return lastRes.current as CanvasOptions;
  });
};

export const setCanvasOption = <K extends keyof CanvasOptions>(
  store: TreeStore,
  key: K,
  val: CanvasOptions[K]
) => {
  const map = store.doc.getMap("options");
  map.set(key, val);
};

export const adaptToGrid = (pos: [number, number], store: TreeStore) => {
  const gridSize = 20;
  // if (gridSize === 1) return pos;
  return [Math.round(pos[0] / gridSize) * gridSize, Math.round(pos[1] / gridSize) * gridSize] as [
    number,
    number,
  ];
};

export const centerOnViewport = (
  bag: VisionBoardBag,
  [width, height]: TwoNums
): VisionBoardNode["pos"] => {
  const {x, y, scale} = bag.posRef.current;
  const [canvasW, canvasH] = bag.canvasMeasureRef.current;
  const [nextX, nextY] = adaptToGrid(
    [(canvasW / scale - width) / 2 - x, (canvasH / scale - height) / 2 - y],
    bag.store
  );
  return {x: nextX, y: nextY, w: width, h: height, parent: null};
};

export const getBezierMidpoint = (
  start: TwoNums,
  startCtrl: TwoNums,
  end: TwoNums,
  endCtrl: TwoNums
): TwoNums => [
  0.125 * start[0] + 0.375 * startCtrl[0] + 0.375 * endCtrl[0] + 0.125 * end[0],
  0.125 * start[1] + 0.375 * startCtrl[1] + 0.375 * endCtrl[1] + 0.125 * end[1],
];

type FourPoints = [TwoNums, TwoNums, TwoNums, TwoNums];
export function evaluateBezier([p1, p2, p3, p4]: FourPoints, t: number): TwoNums {
  const t1 = 1 - t;
  const t1Squared = t1 * t1;
  const tSquared = t * t;

  const x =
    t1Squared * t1 * p1[0] +
    3 * t1Squared * t * p2[0] +
    3 * t1 * tSquared * p3[0] +
    tSquared * t * p4[0];

  const y =
    t1Squared * t1 * p1[1] +
    3 * t1Squared * t * p2[1] +
    3 * t1 * tSquared * p3[1] +
    tSquared * t * p4[1];

  return [x, y];
}

export const getBoundingBox = (nodes: BushRect[]): BushRect => {
  return nodes.reduce(
    (bounds, node) => {
      return {
        minX: Math.min(bounds.minX, node.minX),
        minY: Math.min(bounds.minY, node.minY),
        maxX: Math.max(bounds.maxX, node.maxX),
        maxY: Math.max(bounds.maxY, node.maxY),
      };
    },
    {minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity}
  );
};

export const setInitialPosition = ({
  store,
  canvasMeasureRef,
  boardId,
}: Pick<VisionBoardBag, "store" | "canvasMeasureRef"> & {boardId: VisionBoardId | null}): [
  [number, number],
  number,
] => {
  if (!boardId) return [[0, 0], 1];
  const key = `last-board-position-${boardId}` as "last-board-position-[boardId]";
  const lastStoredVal = getLocalStorageVal(key);
  const [width, height] = canvasMeasureRef.current;
  if (lastStoredVal) {
    const w = width / lastStoredVal.scale;
    const h = height / lastStoredVal.scale;
    const pos: TwoNums = [lastStoredVal.x - w / 2, lastStoredVal.y - h / 2];
    const visibleNodes = store.getNodesSnapshot({
      minX: pos[0],
      minY: pos[1],
      maxX: pos[0] + w,
      maxY: pos[1] + h,
    });
    if (visibleNodes.length > 0) return [pos, lastStoredVal.scale];
  }

  const entries = store.getNodesSnapshot(null);
  let minVal = [Infinity, null] as [number, NodeBushEntry | null];
  for (const entry of entries) {
    const entryVal = entry.minX + entry.minY;
    if (entryVal < minVal[0]) minVal = [entryVal, entry];
  }
  const t = minVal[1];
  if (!t) return [[-width / 2, -height / 2], 1];

  // search all nodes that are top right of target node
  const topSearchRect = {
    minX: t.minX,
    minY: t.maxY - height,
    maxX: t.minX + width,
    maxY: t.maxY,
  };
  const topNodes = store.getNodesSnapshot(topSearchRect);
  const topNodeBounds = getBoundingBox(topNodes);

  // search all ndoes that are bottom left of top-right most nodes
  const leftSearchRect = {
    minY: topNodeBounds.minY,
    maxX: topNodeBounds.maxX,
    maxY: topNodeBounds.minY + height,
    minX: topNodeBounds.maxX - width,
  };
  const leftNodes = store.getNodesSnapshot(leftSearchRect);
  const outerBounds = getBoundingBox(leftNodes);

  if (outerBounds.maxX - outerBounds.minX < width && outerBounds.maxY - outerBounds.minY < height) {
    // if bounds are smaller than viewport look if we could include more nodes by moving further bottom right
    const bottomSearchRect = {
      minX: outerBounds.minX,
      minY: outerBounds.minY,
      maxX: outerBounds.minX + width,
      maxY: outerBounds.minY + height,
    };
    const bottomNodes = store.getNodesSnapshot(bottomSearchRect);
    const bottomOuterBounds = getBoundingBox(bottomNodes);

    const midX = bottomOuterBounds.minX + (bottomOuterBounds.maxX - bottomOuterBounds.minX) / 2;
    const midY = bottomOuterBounds.minY + (bottomOuterBounds.maxY - bottomOuterBounds.minY) / 2;
    return [[midX - width / 2, midY - height / 2], 1];
  } else {
    // if nodes are too massive to be shown on viewport, just show the target at the top left corner
    return [[t.minX - 16, t.minY - 72], 1];
  }
};
